import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    MenuItem,
    Tabs,
    Tab,
    Divider,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useNavigate } from 'react-router-dom';
import {
    getAdminSectors,
    createSector,
    getSectorById,
    updateSector,
    deleteSector,
    getAdminRadarKeywords,
    createRadarKeyword,
    getRadarKeywordById,
    updateRadarKeyword,
    deleteRadarKeyword,
    getAdminEditorialAreas,
    createEditorialArea,
    getEditorialAreaById,
    updateEditorialArea,
    deleteEditorialArea,
} from '../../../../utils/api';
import { createNotification } from '../../../../utils/alerts';
import { getErrorMessage } from '../../../../utils/errorUtils';
import AdminFilters from '../../../../components/admin/AdminFilters';
import ResponsiveAdminPage from '../../../../components/admin/ResponsiveAdminPage';
import ResponsiveDataView from '../../../../components/admin/ResponsiveDataView';

const PRIMARY = '#f25d27';
const PRIMARY_DARK = '#d44e1f';

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const fieldSx = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
    '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
};

const CompactMetaField = ({ label, value }) => (
    <Typography sx={{ fontSize: '0.74rem', color: '#555', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Box>{' '}
        {value || '—'}
    </Typography>
);

const ConfigCompactCard = ({
    row,
    onClick,
    primaryField,
    primaryLabel,
    booleanField = null,
    booleanLabel = 'Ativo',
}) => (
    <Paper
        elevation={0}
        onClick={onClick}
        sx={{
            p: 1.2,
            borderRadius: '8px',
            border: '1px solid #ececec',
            cursor: onClick ? 'pointer' : 'default',
            '&:hover': onClick ? { backgroundColor: `${PRIMARY}08` } : undefined,
        }}
    >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
            <CompactMetaField label={primaryLabel} value={row?.[primaryField] || '—'} />
            {booleanField && (
                <CompactMetaField label={booleanLabel} value={row?.[booleanField] ? 'Sim' : 'Não'} />
            )}
            <CompactMetaField label="Data de criação" value={formatDate(row?.created_at)} />
            <CompactMetaField label="Última atualização" value={formatDate(row?.updated_at)} />
        </Box>
    </Paper>
);

// ── Colunas por aba ─────────────────────────────────────────────────────────

const SECTOR_COLUMNS = [
    { id: 'name', field: 'name', label: 'Nome', minWidth: 180 },
    {
        id: 'created_at', field: 'created_at', label: 'Data de criação', minWidth: 160,
        render: (row) => formatDate(row.created_at),
    },
    {
        id: 'updated_at', field: 'updated_at', label: 'Última atualização', minWidth: 170,
        render: (row) => formatDate(row.updated_at),
    },
];

const RADAR_COLUMNS = [
    { id: 'keyword', field: 'keyword', label: 'Palavra-chave', minWidth: 180 },
    {
        id: 'is_active', field: 'is_active', label: 'Ativo', minWidth: 100, sortable: false,
        render: (row) => (row.is_active ? 'Sim' : 'Não'),
    },
    {
        id: 'created_at', field: 'created_at', label: 'Data de criação', minWidth: 160,
        render: (row) => formatDate(row.created_at),
    },
    {
        id: 'updated_at', field: 'updated_at', label: 'Última atualização', minWidth: 170,
        render: (row) => formatDate(row.updated_at),
    },
];

const EDITORIAL_AREA_COLUMNS = [
    { id: 'name', field: 'name', label: 'Nome', minWidth: 180 },
    {
        id: 'created_at', field: 'created_at', label: 'Data de criação', minWidth: 160,
        render: (row) => formatDate(row.created_at),
    },
    {
        id: 'updated_at', field: 'updated_at', label: 'Última atualização', minWidth: 170,
        render: (row) => formatDate(row.updated_at),
    },
];

// ── Configuração por aba ────────────────────────────────────────────────────

const TAB_CONFIG = [
    {
        label: 'Setores',
        columns: SECTOR_COLUMNS,
        listKey: 'sectors',
        getAll: getAdminSectors,
        create: createSector,
        getById: getSectorById,
        update: updateSector,
        remove: deleteSector,
        booleanField: null,
        primaryField: 'name',
        primaryLabel: 'Nome',
        entityLabel: 'setor',
        hidden: true,
    },
    {
        label: 'Radar',
        columns: RADAR_COLUMNS,
        listKey: 'radar_keywords',
        getAll: getAdminRadarKeywords,
        create: createRadarKeyword,
        getById: getRadarKeywordById,
        update: updateRadarKeyword,
        remove: deleteRadarKeyword,
        booleanField: 'is_active',
        booleanLabel: 'Ativo',
        primaryField: 'keyword',
        primaryLabel: 'Palavra-chave',
        entityLabel: 'radar',
        hasActiveFilter: true,
    },
    {
        label: 'Áreas Editoriais',
        columns: EDITORIAL_AREA_COLUMNS,
        listKey: 'editorial_areas',
        getAll: getAdminEditorialAreas,
        create: createEditorialArea,
        getById: getEditorialAreaById,
        update: updateEditorialArea,
        remove: deleteEditorialArea,
        booleanField: null,
        primaryField: 'name',
        primaryLabel: 'Nome',
        entityLabel: 'área editorial',
    },
];

const VISIBLE_TAB_CONFIG = TAB_CONFIG.filter((cfg) => !cfg.hidden);

// ── Componente de uma aba CRUD ──────────────────────────────────────────────

const CrudTab = forwardRef(function CrudTab({ config, isMobile }, ref) {
    const isCardsView = useMediaQuery('(max-width:1024px)');
    const {
        columns,
        listKey,
        getAll,
        create,
        getById,
        update,
        remove,
        booleanField,
        booleanLabel = 'Ativo',
        primaryField = 'name',
        primaryLabel = 'Nome',
        entityLabel,
        hasActiveFilter = false,
    } = config;

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [orderBy, setOrderBy] = useState('created_at');
    const [orderDirection, setOrderDirection] = useState('desc');
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // modal de detalhes / edição
    const [detailItem, setDetailItem] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editIsRelevant, setEditIsRelevant] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

    // modal de criação
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState('');
    const [newDescription, setNewDescription] = useState('');
    const [newIsRelevant, setNewIsRelevant] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    // debounce da busca
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch(searchInput);
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, search, orderBy, orderDirection, activeFilter]);

    const fetchRows = async () => {
        setLoading(true);
        try {
            const params = { order_by: orderBy, order_direction: orderDirection, page, page_size: pageSize };
            if (search) params.search = search;
            if (hasActiveFilter && activeFilter !== '') params.is_active = activeFilter === 'true';
            const data = await getAll(params);
            setRows(data[listKey] ?? []);
            setTotal(data.total);
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', `Erro ao carregar ${entityLabel}s.`, isMobile);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (orderBy === field) {
            setOrderDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setOrderBy(field);
            setOrderDirection('asc');
        }
        setPage(0);
    };

    // Abre modal de detalhes e carrega descrição via GET /{id}
    const handleRowClick = async (row) => {
        setDetailItem(row);
        setDetailLoading(true);
        setEditName(row[primaryField] || '');
        setEditDescription('');
        setEditIsRelevant(booleanField ? row[booleanField] ?? false : false);
        try {
            const detail = await getById(row.id);
            setEditDescription(detail.description || '');
            setEditName(detail[primaryField] || row[primaryField] || '');
            setEditIsRelevant(booleanField ? detail[booleanField] ?? false : false);
        } catch {
            // mantém valores já carregados
        } finally {
            setDetailLoading(false);
        }
    };

    const handleCloseDetail = () => {
        if (updateLoading || deleteLoading) return;
        setDetailItem(null);
        setConfirmDeleteOpen(false);
    };

    const handleUpdate = async () => {
        setUpdateLoading(true);
        try {
            const payload = { [primaryField]: editName, description: editDescription };
            if (booleanField) payload[booleanField] = editIsRelevant;
            await update(detailItem.id, payload);
            createNotification('success', `${capitalize(entityLabel)} atualizado com sucesso!`, isMobile);
            handleCloseDetail();
            fetchRows();
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, `Erro ao atualizar ${entityLabel}.`), isMobile);
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        try {
            await remove(detailItem.id);
            createNotification('success', `${capitalize(entityLabel)} excluído com sucesso!`, isMobile);
            setConfirmDeleteOpen(false);
            setDetailItem(null);
            fetchRows();
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, `Erro ao excluir ${entityLabel}.`), isMobile);
            }
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleOpenCreate = () => {
        setNewName('');
        setNewDescription('');
        setNewIsRelevant(false);
        setCreateOpen(true);
    };

    useImperativeHandle(ref, () => ({ openCreate: handleOpenCreate }));

    const handleCloseCreate = () => {
        if (createLoading) return;
        setCreateOpen(false);
    };

    const handleCreate = async () => {
        setCreateLoading(true);
        try {
            const payload = { [primaryField]: newName, description: newDescription };
            if (booleanField) payload[booleanField] = newIsRelevant;
            await create(payload);
            createNotification('success', `${capitalize(entityLabel)} criado com sucesso!`, isMobile);
            setCreateOpen(false);
            fetchRows();
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', getErrorMessage(err, `Erro ao criar ${entityLabel}.`), isMobile);
            }
        } finally {
            setCreateLoading(false);
        }
    };

    return (
        <Box>
            {/* Filtro full-width */}
            <Box sx={{ mb: 2 }}>
                <AdminFilters
                    search={searchInput}
                    onSearchChange={setSearchInput}
                    placeholder={`Pesquise por ${primaryLabel.toLowerCase()}`}
                >
                    {hasActiveFilter && (
                        <TextField
                            select
                            size="small"
                            label="Ativo"
                            value={activeFilter}
                            onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }}
                            sx={{
                                minWidth: 140,
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f25d27' },
                                '& .MuiInputLabel-root.Mui-focused': { color: '#f25d27' },
                            }}
                        >
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="true">Ativo</MenuItem>
                            <MenuItem value="false">Inativo</MenuItem>
                        </TextField>
                    )}
                </AdminFilters>
            </Box>

            <Paper
                sx={{
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                    p: isCardsView ? 1 : 0,
                }}
            >
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress sx={{ color: PRIMARY }} />
                    </Box>
                ) : (
                    <ResponsiveDataView
                        columns={columns}
                        rows={rows}
                        orderBy={orderBy}
                        orderDirection={orderDirection}
                        onSort={handleSort}
                        onRowClick={handleRowClick}
                        mobileBreakpoint={1024}
                        mobileCardRenderer={(row, index) => (
                            <ConfigCompactCard
                                key={row.id ?? index}
                                row={row}
                                onClick={() => handleRowClick(row)}
                                primaryField={primaryField}
                                primaryLabel={primaryLabel}
                                booleanField={booleanField}
                                booleanLabel={booleanLabel}
                            />
                        )}
                        emptyMessage={`Nenhum ${entityLabel} encontrado.`}
                        page={page}
                        pageSize={pageSize}
                        total={total}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
                        paperSx={{ borderRadius: '8px', border: 'none', boxShadow: 'none' }}
                    />
                )}
            </Paper>

            {/* ── Modal: Detalhes / Edição ──────────────────────────────── */}
            <Dialog
                open={Boolean(detailItem)}
                onClose={handleCloseDetail}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Detalhes do {entityLabel}
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    {detailLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: PRIMARY }} size={28} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label={primaryLabel}
                                size="small"
                                fullWidth
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                disabled={updateLoading}
                                sx={fieldSx}
                            />
                            <TextField
                                label="Descrição"
                                size="small"
                                fullWidth
                                multiline
                                minRows={2}
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                disabled={updateLoading}
                                sx={fieldSx}
                            />
                            {booleanField && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={editIsRelevant}
                                            onChange={(e) => setEditIsRelevant(e.target.checked)}
                                            disabled={updateLoading}
                                            sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                                        />
                                    }
                                    label={booleanLabel}
                                />
                            )}
                            <Divider />
                            <Box sx={{ display: 'flex', gap: 3 }}>
                                {detailItem?.created_at && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                            Data de criação
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#333' }}>
                                            {formatDate(detailItem.created_at)}
                                        </Typography>
                                    </Box>
                                )}
                                {detailItem?.updated_at && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>
                                            Última atualização
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#333' }}>
                                            {formatDate(detailItem.updated_at)}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmDeleteOpen(true)}
                        disabled={updateLoading || deleteLoading || detailLoading}
                        sx={{ color: '#d32f2f', textTransform: 'none', '&:hover': { backgroundColor: 'rgba(211,47,47,0.06)' } }}
                    >
                        Excluir
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={handleCloseDetail}
                        disabled={updateLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Fechar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleUpdate}
                        disabled={updateLoading || detailLoading || !editName || !editDescription}
                        sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none', borderRadius: '8px' }}
                    >
                        {updateLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Atualizar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Modal: Confirmação de exclusão ───────────────────────── */}
            <Dialog
                open={confirmDeleteOpen}
                onClose={() => !deleteLoading && setConfirmDeleteOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Deseja realmente excluir {artigo(entityLabel)} <strong>{detailItem?.[primaryField]}</strong>? Esta ação não poderá ser desfeita.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setConfirmDeleteOpen(false)}
                        disabled={deleteLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteLoading}
                        sx={{ backgroundColor: '#d32f2f', textTransform: 'none', boxShadow: 'none', borderRadius: '8px', '&:hover': { backgroundColor: '#b71c1c' } }}
                    >
                        {deleteLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Excluir'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Modal: Criar ─────────────────────────────────────────── */}
            <Dialog
                open={createOpen}
                onClose={handleCloseCreate}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>
                    Novo {entityLabel}
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
                        <TextField
                            label={primaryLabel}
                            size="small"
                            fullWidth
                            autoFocus
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            disabled={createLoading}
                            sx={fieldSx}
                        />
                        <TextField
                            label="Descrição"
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            value={newDescription}
                            onChange={(e) => setNewDescription(e.target.value)}
                            disabled={createLoading}
                            sx={fieldSx}
                        />
                        {booleanField && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={newIsRelevant}
                                        onChange={(e) => setNewIsRelevant(e.target.checked)}
                                        disabled={createLoading}
                                        sx={{ color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                                    />
                                }
                                label={booleanLabel}
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={handleCloseCreate}
                        disabled={createLoading}
                        sx={{ color: '#666', textTransform: 'none' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreate}
                        disabled={!newName || !newDescription || createLoading}
                        sx={{ backgroundColor: PRIMARY, '&:hover': { backgroundColor: PRIMARY_DARK }, textTransform: 'none', boxShadow: 'none', borderRadius: '8px' }}
                    >
                        {createLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Criar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}); // end forwardRef CrudTab

// ── Helpers ─────────────────────────────────────────────────────────────────

function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function artigo(entityLabel) {
    const femininoPrefixes = ['área', 'a'];
    const word = entityLabel.toLowerCase();
    return femininoPrefixes.some((p) => word.startsWith(p)) ? 'a' : 'o';
}

// ── Componente principal ─────────────────────────────────────────────────────

export default function AdminConfigEditorialIntelligencePage() {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    const isCompactHeader = useMediaQuery('(max-width:1024px)');
    const [tab, setTab] = useState(0);
    const tabRefs = useRef([]);

    const activeConfig = VISIBLE_TAB_CONFIG[tab];

    return (
        <ResponsiveAdminPage sx={isCompactHeader ? { pt: '16px' } : undefined}>
            <Box sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1, mb: 0.4 }}>
                    Configurações
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: '#999', fontSize: '0.78rem' }}>
                        Gerencie radar e áreas editoriais
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/admin/editorial-intelligence')}
                            sx={{ color: '#434851', textTransform: 'none', pl: 0 }}
                        >
                            Voltar para inteligência editorial
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon fontSize="small" />}
                            onClick={() => tabRefs.current[tab]?.openCreate()}
                            sx={{
                                backgroundColor: PRIMARY,
                                '&:hover': { backgroundColor: PRIMARY_DARK },
                                textTransform: 'none',
                                boxShadow: 'none',
                                fontWeight: 600,
                            }}
                        >
                            Novo {activeConfig.entityLabel}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                sx={{
                    mb: 3,
                    '& .MuiTabs-indicator': { backgroundColor: PRIMARY },
                    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 },
                    '& .Mui-selected': { color: `${PRIMARY} !important` },
                }}
            >
                {VISIBLE_TAB_CONFIG.map((cfg) => (
                    <Tab key={cfg.label} label={cfg.label} />
                ))}
            </Tabs>

            {VISIBLE_TAB_CONFIG.map((cfg, idx) => (
                tab === idx && (
                    <CrudTab
                        key={cfg.label}
                        ref={(el) => { tabRefs.current[idx] = el; }}
                        config={cfg}
                        isMobile={isMobile}
                    />
                )
            ))}
        </ResponsiveAdminPage>
    );
}
